package horcrux.common.api.dtos.types

import arrow.core.Either
import arrow.core.flatMap
import arrow.core.getOrElse
import arrow.core.raise.either
import horcrux.common.DocumentTypeSerializer
import horcrux.common.HorcruxRequestSimpleError
import horcrux.common.ValidatedValueClass
import horcrux.common.api.dtos.DocumentChunkerType
import horcrux.common.types.DocumentContainer
import horcrux.common.types.DocumentTypeIdentifier
import horcrux.common.types.MimeType
import horcrux.common.utils.raiseError
import io.ktor.http.HttpStatusCode
import kotlinx.serialization.Serializable
import kotlin.jvm.JvmInline

@Serializable(with = DocumentTypeSerializer::class)
enum class DocumentType(
    val documentTypeIdentifier: DocumentTypeIdentifier,
    val displayName: String,
    val defaultDocumentChunkerType: DocumentChunkerType,
) {
    CSS(MimeType.CSS, "CSS", DocumentChunkerType.TEXT),
    CSV(MimeType.CSV, "CSV", DocumentChunkerType.TEXT),
    HTML(MimeType.TEXT_HTML, "HTML", DocumentChunkerType.TEXT),
    ICS(MimeType.ICS, "ICS", DocumentChunkerType.TEXT),
    IMAGE_JPEG(MimeType.IMAGE_JPEG, "JPEG Image", DocumentChunkerType.IMAGE),
    IMAGE_PNG(MimeType.IMAGE_PNG, "PNG", DocumentChunkerType.IMAGE),
    INSTAGRAM_ACCOUNT(DocumentContainer, "Instagram Account", DocumentChunkerType.COMPOSITE),
    INSTAGRAM_POST(DocumentContainer, "Instagram Post", DocumentChunkerType.COMPOSITE),
    JAVASCRIPT(MimeType.JAVASCRIPT, "JavasScript", DocumentChunkerType.TEXT),
    JSON(MimeType.APPLICATION_JSON, "JSON", DocumentChunkerType.TEXT),
    PDF(MimeType.APPLICATION_PDF, "PDF", DocumentChunkerType.TEXT),
    PLAIN_TEXT(MimeType.TEXT_PLAIN, "Text", DocumentChunkerType.TEXT),
    VIDEO_MP4(MimeType.VIDEO_MP4, "MP4 Video", DocumentChunkerType.IMAGE),
    XML(MimeType.XML, "XML", DocumentChunkerType.TEXT);

    val documentTypeName: DocumentTypeName
        get() = DocumentTypeName(name)

    val mimeType: MimeType
        get() = when (documentTypeIdentifier) {
            is DocumentContainer -> MimeType.APPLICATION_JSON
            is MimeType -> documentTypeIdentifier
        }


    companion object {
        private val documentTypesWithMimeTypeSourceType: List<DocumentType> =
            entries.toList().filter { it.documentTypeIdentifier is MimeType }
        private val documentTypeByMimeType: Map<MimeType, DocumentType> = documentTypesWithMimeTypeSourceType
            .mapNotNull {
                when (val documentTypeIdentifier = it.documentTypeIdentifier) {
                    is DocumentContainer -> null
                    is MimeType -> documentTypeIdentifier to it
                }
            }
            .toMap()
        private val documentTypeByName: Map<DocumentTypeName, DocumentType> =
            entries.associateBy { it.documentTypeName }

        data class InvalidMimeType(val mimeType: MimeType) :
            HorcruxRequestSimpleError(HttpStatusCode.UnsupportedMediaType)

        operator fun invoke(mimeType: MimeType): Either<Throwable, DocumentType> =
            either { documentTypeByMimeType[mimeType] ?: raise(InvalidMimeType(mimeType)) }

        operator fun invoke(mimeType: String): Either<Throwable, DocumentType> =
            MimeType.invoke(mimeType)
                .flatMap { DocumentType.invoke(it) }

        operator fun invoke(mimeType: String, default: DocumentType): DocumentType =
            MimeType.invoke(mimeType)
                .flatMap { DocumentType.invoke(it) }
                .getOrElse { default }

        operator fun invoke(documentTypeName: DocumentTypeName): Either<Throwable, DocumentType> = either {
            documentTypeByName[documentTypeName]
                ?: raiseError("$documentTypeName was not found. Valid entries are ${documentTypeByName.keys}.")
        }
    }
}

@JvmInline
value class DocumentTypeName(override val value: String) : ValidatedValueClass<String, DocumentTypeName>
